import React, { useState, useEffect, useMemo } from 'react';
import { SurveyDefinition, DynamicSurveyResponse, QuestionDefinition } from '../../types/survey.types';
import { useResponses } from '../../hooks/useResponses';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, LabelList,
  PieChart, Pie, LineChart, Line, AreaChart, Area, Legend
} from 'recharts';
import ChartTypeSelector, { ChartType } from './ChartTypeSelector';
import InsightsPanel from './InsightsPanel';
import OutlierModal from './OutlierModal';
import { generateInsights, Insight, QuestionAnalyticsInput } from '../../utils/analyticsInsights';

// Chart color palette
const CHART_COLORS = [
  '#264372',  // Primary blue
  '#C7A14A',  // Accent gold
  '#2B7A78',  // Teal
  '#7C3AED',  // Purple
  '#059669',  // Emerald
  '#DC2626',  // Red
  '#F59E0B',  // Amber
  '#EC4899',  // Pink
];

interface ChartInsightsProps {
  surveyId: string;
  survey: SurveyDefinition;
  onExportPDF?: () => void;
}

interface ConditionalContext {
  isConditional: boolean;
  eligibleCount: number;
  description: string;
}

interface DistributionEntry {
  count: number;
  percentOfAnswered: number;
  percentOfTotal: number;
}

interface TextResponseEntry {
  text: string;
  responseIndex: number;
  wordCount: number;
}

interface QuestionAnalytics {
  questionId: string;
  questionTitle: string;
  questionType: string;
  responseCount: number;
  totalSurveyRespondents: number;
  eligibleRespondents: number;
  skipCount: number;
  notShownCount: number;
  conditionalContext?: ConditionalContext;
  distribution?: Record<string, DistributionEntry>;
  allOptions?: string[];
  numericStats?: {
    min: number;
    max: number;
    average: number;
    median: number;
    stdDev: number;
    mode: number;
  };
  textResponses?: TextResponseEntry[];
}

const calculateStdDev = (values: number[]): number => {
  if (values.length === 0) return 0;
  const mean = values.reduce((a, b) => a + b, 0) / values.length;
  const squaredDiffs = values.map(v => Math.pow(v - mean, 2));
  const variance = squaredDiffs.reduce((a, b) => a + b, 0) / values.length;
  return Math.sqrt(variance);
};

const calculateMode = (values: number[]): number => {
  if (values.length === 0) return 0;
  const frequency: Record<number, number> = {};
  let maxFreq = 0;
  let mode = values[0];
  values.forEach(v => {
    frequency[v] = (frequency[v] || 0) + 1;
    if (frequency[v] > maxFreq) {
      maxFreq = frequency[v];
      mode = v;
    }
  });
  return mode;
};

const ChartInsights: React.FC<ChartInsightsProps> = ({ surveyId, survey, onExportPDF }) => {
  const [analytics, setAnalytics] = useState<QuestionAnalytics[]>([]);
  const [loading, setLoading] = useState(true);
  const [showRawData, setShowRawData] = useState(false);
  const [rawResponses, setRawResponses] = useState<DynamicSurveyResponse[]>([]);
  const [chartTypes, setChartTypes] = useState<Record<string, ChartType>>({});
  const { getResponses } = useResponses();

  // Outlier exclusion state (session-only, not persisted)
  const [excludedOutliers, setExcludedOutliers] = useState<Record<string, Set<number>>>({});
  const [outlierModalQuestionId, setOutlierModalQuestionId] = useState<string | null>(null);

  // Handler to apply outlier exclusions
  const handleApplyOutliers = (questionId: string, excludedIndices: Set<number>) => {
    setExcludedOutliers(prev => ({
      ...prev,
      [questionId]: excludedIndices
    }));
    setOutlierModalQuestionId(null);
  };

  // Handler to clear outlier exclusions for a question
  const handleClearOutliers = (questionId: string) => {
    setExcludedOutliers(prev => {
      const updated = { ...prev };
      delete updated[questionId];
      return updated;
    });
  };

  // Get numeric values for a question (for outlier modal)
  const getNumericValuesForQuestion = (questionId: string): Array<{ value: number; responseIndex: number }> => {
    return rawResponses
      .map((r, idx) => {
        const response = r.responses[questionId];
        if (!response || response.value === null || response.value === undefined || response.skipped) {
          return null;
        }
        const numValue = typeof response.value === 'number' ? response.value : parseFloat(String(response.value));
        if (isNaN(numValue)) return null;
        return { value: numValue, responseIndex: idx };
      })
      .filter((item): item is { value: number; responseIndex: number } => item !== null);
  };

  // Generate AI insights from analytics data
  const insights = useMemo<Insight[]>(() => {
    if (analytics.length === 0) return [];
    const inputData: QuestionAnalyticsInput[] = analytics.map(q => ({
      questionId: q.questionId,
      questionTitle: q.questionTitle,
      questionType: q.questionType,
      responseCount: q.responseCount,
      totalSurveyRespondents: q.totalSurveyRespondents,
      eligibleRespondents: q.eligibleRespondents,
      skipCount: q.skipCount,
      distribution: q.distribution,
      numericStats: q.numericStats,
      textResponses: q.textResponses,
    }));
    return generateInsights(inputData, analytics[0]?.totalSurveyRespondents || 0);
  }, [analytics]);

  // Get chart type for a question with smart defaults
  const getChartType = (questionId: string, questionType: string): ChartType => {
    if (chartTypes[questionId]) return chartTypes[questionId];
    // Smart defaults by question type
    if (questionType === 'yesno') return 'donut';
    if (['number', 'rating', 'scale'].includes(questionType)) return 'bar';
    return 'horizontalBar';
  };

  const handleChartTypeChange = (questionId: string, chartType: ChartType) => {
    setChartTypes(prev => ({ ...prev, [questionId]: chartType }));
  };

  useEffect(() => {
    loadAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surveyId]);

  const getQuestionMetrics = (
    question: QuestionDefinition,
    allResponses: DynamicSurveyResponse[],
    totalSurveyRespondents: number
  ): {
    responseCount: number;
    eligibleRespondents: number;
    skipCount: number;
    notShownCount: number;
    conditionalContext: ConditionalContext;
    validResponses: Array<{ value: unknown; responseIndex: number }>;
  } => {
    const hasLogicRules = question.logicRules && question.logicRules.length > 0;
    const allQuestions = survey.sections.flatMap(s => s.questions);

    let eligibleRespondents = totalSurveyRespondents;
    let description = 'All respondents';
    let isConditional = false;

    if (hasLogicRules) {
      isConditional = true;
      description = 'Conditional question';
      const firstRule = question.logicRules?.find(r => r.enabled);
      if (firstRule && firstRule.ruleGroups && firstRule.ruleGroups.length > 0) {
        const firstGroup = firstRule.ruleGroups[0];
        if (firstGroup.conditions && firstGroup.conditions.length > 0) {
          const firstCondition = firstGroup.conditions[0];
          const refQuestion = allQuestions.find(q => q.id === firstCondition.questionId);
          if (refQuestion) {
            description = `Based on "${refQuestion.title}"`;
          }
        }
      }

      eligibleRespondents = allResponses.filter((r) => {
        if (r.responses[question.id] !== undefined) return true;
        return false;
      }).length;

      if (eligibleRespondents === 0) {
        eligibleRespondents = Math.floor(totalSurveyRespondents * 0.5);
      }
    }

    const validResponses: Array<{ value: unknown; responseIndex: number }> = [];
    allResponses.forEach((r, idx) => {
      const response = r.responses[question.id];
      if (response !== undefined && response !== null && response.value !== undefined && response.value !== null && response.value !== '') {
        if (response.skipped) return;
        validResponses.push({ value: response.value, responseIndex: idx + 1 });
      }
    });

    const responseCount = validResponses.length;
    const skipCount = Math.max(0, eligibleRespondents - responseCount);
    const notShownCount = Math.max(0, totalSurveyRespondents - eligibleRespondents);

    return {
      responseCount,
      eligibleRespondents: isConditional ? eligibleRespondents : totalSurveyRespondents,
      skipCount,
      notShownCount: isConditional ? notShownCount : 0,
      conditionalContext: {
        isConditional,
        eligibleCount: eligibleRespondents,
        description
      },
      validResponses
    };
  };

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      const responses = await getResponses(surveyId);

      if (responses.length === 0) {
        setAnalytics([]);
        setLoading(false);
        return;
      }

      const totalSurveyRespondents = responses.length;
      const analyticsData: QuestionAnalytics[] = [];
      setRawResponses(responses);

      survey.sections.forEach(section => {
        section.questions.forEach(question => {
          const metrics = getQuestionMetrics(question, responses, totalSurveyRespondents);

          const questionAnalytics: QuestionAnalytics = {
            questionId: question.id,
            questionTitle: question.title,
            questionType: question.type,
            responseCount: metrics.responseCount,
            totalSurveyRespondents,
            eligibleRespondents: metrics.eligibleRespondents,
            skipCount: metrics.skipCount,
            notShownCount: metrics.notShownCount,
            conditionalContext: metrics.conditionalContext,
          };

          if (['radio', 'checkbox', 'dropdown', 'yesno'].includes(question.type)) {
            const rawDistribution: Record<string, number> = {};
            const allOptions: string[] = [];

            if (question.type === 'yesno') {
              allOptions.push('Yes', 'No');
              rawDistribution['Yes'] = 0;
              rawDistribution['No'] = 0;
            } else if (question.options && question.options.length > 0) {
              question.options.forEach(opt => {
                allOptions.push(opt.value);
                rawDistribution[opt.value] = 0;
              });
            }

            metrics.validResponses.forEach(({ value }) => {
              if (Array.isArray(value)) {
                value.forEach(v => {
                  const key = String(v);
                  rawDistribution[key] = (rawDistribution[key] || 0) + 1;
                });
              } else {
                // Normalize yes/no values to "Yes"/"No"
                let key: string;
                if (question.type === 'yesno') {
                  // Handle boolean true/false, string "true"/"false", "yes"/"no", "Yes"/"No"
                  const normalizedValue = String(value).toLowerCase();
                  key = (normalizedValue === 'true' || normalizedValue === 'yes') ? 'Yes' : 'No';
                } else {
                  key = String(value);
                }
                rawDistribution[key] = (rawDistribution[key] || 0) + 1;
              }
            });

            const distribution: Record<string, DistributionEntry> = {};
            Object.entries(rawDistribution).forEach(([key, count]) => {
              distribution[key] = {
                count,
                percentOfAnswered: metrics.responseCount > 0 ? (count / metrics.responseCount) * 100 : 0,
                percentOfTotal: totalSurveyRespondents > 0 ? (count / totalSurveyRespondents) * 100 : 0
              };
            });

            questionAnalytics.distribution = distribution;
            questionAnalytics.allOptions = allOptions.length > 0 ? allOptions : Object.keys(rawDistribution);
          }

          if (['number', 'rating', 'scale'].includes(question.type)) {
            const numericValues = metrics.validResponses
              .map(r => typeof r.value === 'number' ? r.value : parseFloat(String(r.value)))
              .filter(v => !isNaN(v));

            if (numericValues.length > 0) {
              const sorted = [...numericValues].sort((a, b) => a - b);
              const sum = numericValues.reduce((a, b) => a + b, 0);
              const average = sum / numericValues.length;

              const rawDistribution: Record<string, number> = {};
              numericValues.forEach(val => {
                const key = String(val);
                rawDistribution[key] = (rawDistribution[key] || 0) + 1;
              });

              const distribution: Record<string, DistributionEntry> = {};
              Object.entries(rawDistribution).forEach(([key, count]) => {
                distribution[key] = {
                  count,
                  percentOfAnswered: numericValues.length > 0 ? (count / numericValues.length) * 100 : 0,
                  percentOfTotal: totalSurveyRespondents > 0 ? (count / totalSurveyRespondents) * 100 : 0
                };
              });

              questionAnalytics.distribution = distribution;
              questionAnalytics.numericStats = {
                min: sorted[0],
                max: sorted[sorted.length - 1],
                average,
                median: sorted.length % 2 === 0
                  ? (sorted[sorted.length / 2 - 1] + sorted[sorted.length / 2]) / 2
                  : sorted[Math.floor(sorted.length / 2)],
                stdDev: calculateStdDev(numericValues),
                mode: calculateMode(numericValues)
              };
            } else {
              questionAnalytics.distribution = {};
            }
          }

          if (['text', 'textarea', 'email'].includes(question.type)) {
            const textResponses: TextResponseEntry[] = metrics.validResponses
              .map(({ value, responseIndex }) => {
                const text = String(value).trim();
                return {
                  text,
                  responseIndex,
                  wordCount: text.split(/\s+/).filter(w => w.length > 0).length
                };
              })
              .filter(r => r.text !== '');

            questionAnalytics.textResponses = textResponses;
          }

          analyticsData.push(questionAnalytics);
        });
      });

      setAnalytics(analyticsData);
    } catch (error) {
      console.error('[ChartInsights] Error loading analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 mx-auto" style={{ borderBottom: '2px solid var(--color-accent)', borderTop: '2px solid transparent', borderLeft: '2px solid transparent', borderRight: '2px solid transparent' }}></div>
        <p className="mt-4 text-sm" style={{ color: 'var(--color-muted)' }}>Loading insights...</p>
      </div>
    );
  }

  if (analytics.length === 0) {
    return (
      <div className="text-center py-12">
        <p style={{ fontSize: '1rem', color: 'var(--color-muted)' }}>No analytics data yet</p>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6 pb-4" style={{ borderBottom: '1px solid var(--color-border)' }}>
        <div>
          <h3 style={{ fontFamily: 'var(--font-heading)', fontSize: '1.125rem', fontWeight: 600, color: 'var(--color-primary)' }}>
            Analytics Overview
          </h3>
          <p className="text-sm mt-1" style={{ color: 'var(--color-muted)' }}>
            {analytics[0]?.totalSurveyRespondents || 0} total responses
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={loadAnalytics}
            className="text-sm px-3 py-1.5 rounded-md transition-colors hover:bg-gray-100"
            style={{ color: 'var(--color-muted)' }}
          >
            Refresh
          </button>
          {onExportPDF && (
            <button
              onClick={onExportPDF}
              className="text-sm font-medium px-4 py-1.5 rounded-md transition-colors"
              style={{ backgroundColor: 'var(--color-accent)', color: 'white' }}
            >
              Export PDF
            </button>
          )}
        </div>
      </div>

      {/* AI Insights Panel */}
      <InsightsPanel
        insights={insights}
        totalResponses={analytics[0]?.totalSurveyRespondents || 0}
      />

      {/* Questions List */}
      <div className="space-y-1">
        {survey.sections.map((section, sectionIndex) => (
          <div key={section.id}>
            {/* Section Header */}
            <div className="py-2 px-1 mb-2" style={{ backgroundColor: 'rgba(0,0,0,0.02)' }}>
              <h4 style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--color-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {section.title}
              </h4>
            </div>

            {/* Questions in Section */}
            {section.questions.map((question, qIndex) => {
              const qa = analytics.find(a => a.questionId === question.id);
              if (!qa) return null;
              const questionInsights = insights.filter(i => i.questionId === question.id);

              return (
                <QuestionRow
                  key={question.id}
                  question={question}
                  analytics={qa}
                  questionNumber={`${sectionIndex + 1}.${qIndex + 1}`}
                  rawResponses={rawResponses}
                  chartType={getChartType(question.id, question.type)}
                  onChartTypeChange={(type) => handleChartTypeChange(question.id, type)}
                  questionInsights={questionInsights}
                  excludedOutliers={excludedOutliers[question.id] || new Set()}
                  onOpenOutlierModal={() => setOutlierModalQuestionId(question.id)}
                  onClearOutliers={() => handleClearOutliers(question.id)}
                />
              );
            })}
          </div>
        ))}
      </div>

      {/* Raw Data Section */}
      <div className="mt-8 pt-6" style={{ borderTop: '2px solid var(--color-border)' }}>
        <div className="flex items-center justify-between mb-4">
          <h3 style={{ fontFamily: 'var(--font-heading)', fontSize: '1rem', fontWeight: 600, color: 'var(--color-primary)' }}>
            Raw Response Data
          </h3>
          <button
            onClick={() => setShowRawData(!showRawData)}
            className="text-sm font-medium px-3 py-1.5 rounded-md transition-colors"
            style={{ backgroundColor: showRawData ? 'var(--color-accent)' : 'rgba(0,0,0,0.05)', color: showRawData ? 'white' : 'var(--color-muted)' }}
          >
            {showRawData ? 'Hide Raw Data' : 'Show All Responses'}
          </button>
        </div>

        {showRawData && rawResponses.length > 0 && (
          <div className="space-y-4">
            {survey.sections.map((section) => (
              <div key={section.id}>
                <h4 className="text-xs font-semibold uppercase tracking-wide mb-2" style={{ color: 'var(--color-muted)' }}>
                  {section.title}
                </h4>
                {section.questions.map((question) => {
                  const allAnswers = rawResponses
                    .map((r, idx) => {
                      const response = r.responses[question.id];
                      if (!response || response.value === null || response.value === undefined || response.value === '') {
                        return { idx: idx + 1, value: null };
                      }
                      return { idx: idx + 1, value: response.value };
                    })
                    .filter(a => a.value !== null);

                  return (
                    <div key={question.id} className="mb-4 pb-4" style={{ borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p style={{ fontSize: '0.8125rem', fontWeight: 500, color: 'var(--color-primary)' }}>
                            {question.title}
                          </p>
                          {/* Scale indicator for rating/scale questions - shows the question's defined scale */}
                          {['rating', 'scale'].includes(question.type) && (
                            <span
                              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs"
                              style={{ backgroundColor: 'rgba(199, 161, 74, 0.15)', color: '#b8860b', fontWeight: 500 }}
                            >
                              Scale: {question.settings?.minValue ?? 1}-{question.settings?.maxValue ?? 5}
                            </span>
                          )}
                        </div>
                        <span style={{ fontSize: '0.6875rem', color: 'var(--color-muted)' }}>
                          {allAnswers.length} responses
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {allAnswers.length > 0 ? (
                          allAnswers.map((answer, i) => (
                            <span
                              key={i}
                              className="inline-flex items-center px-2 py-1 rounded text-xs"
                              style={{ backgroundColor: 'rgba(199, 161, 74, 0.1)', color: 'var(--color-primary)' }}
                              title={`Response #${answer.idx}`}
                            >
                              {Array.isArray(answer.value)
                                ? answer.value.join(', ')
                                : typeof answer.value === 'boolean'
                                  ? answer.value ? 'Yes' : 'No'
                                  : String(answer.value).length > 50
                                    ? String(answer.value).substring(0, 50) + '...'
                                    : String(answer.value)}
                            </span>
                          ))
                        ) : (
                          <span style={{ fontSize: '0.75rem', color: 'var(--color-muted)', fontStyle: 'italic' }}>
                            No responses
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Outlier Modal */}
      {outlierModalQuestionId && (() => {
        const modalQuestion = survey.sections.flatMap(s => s.questions).find(q => q.id === outlierModalQuestionId);
        if (!modalQuestion) return null;
        return (
          <OutlierModal
            isOpen={true}
            onClose={() => setOutlierModalQuestionId(null)}
            questionId={outlierModalQuestionId}
            questionTitle={modalQuestion.title}
            numericValues={getNumericValuesForQuestion(outlierModalQuestionId)}
            currentExclusions={excludedOutliers[outlierModalQuestionId] || new Set()}
            onApply={(excludedIndices) => handleApplyOutliers(outlierModalQuestionId, excludedIndices)}
          />
        );
      })()}
    </div>
  );
};

// Clean, compact question row component
interface QuestionRowProps {
  question: QuestionDefinition;
  analytics: QuestionAnalytics;
  questionNumber: string;
  rawResponses: DynamicSurveyResponse[];
  chartType: ChartType;
  onChartTypeChange: (chartType: ChartType) => void;
  questionInsights: Insight[];
  excludedOutliers: Set<number>;
  onOpenOutlierModal: () => void;
  onClearOutliers: () => void;
}

const QuestionRow: React.FC<QuestionRowProps> = ({
  question,
  analytics,
  questionNumber,
  rawResponses,
  chartType,
  onChartTypeChange,
  questionInsights,
  excludedOutliers,
  onOpenOutlierModal,
  onClearOutliers,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [showAllText, setShowAllText] = useState(false);
  const [showAllResponses, setShowAllResponses] = useState(false);

  // Calculate filtered analytics when outliers are excluded
  const filteredAnalytics = useMemo(() => {
    if (excludedOutliers.size === 0 || !['number', 'rating', 'scale'].includes(question.type)) {
      return analytics;
    }

    // Get original numeric values (excluding outliers)
    const filteredDistribution: Record<string, DistributionEntry> = {};
    let totalFilteredCount = 0;
    const filteredNumericValues: number[] = [];

    // Reconstruct distribution from rawResponses, excluding outliers
    rawResponses.forEach((r, idx) => {
      if (excludedOutliers.has(idx)) return;

      const response = r.responses[question.id];
      if (!response || response.value === null || response.value === undefined || response.skipped) return;

      const numValue = typeof response.value === 'number' ? response.value : parseFloat(String(response.value));
      if (isNaN(numValue)) return;

      const key = String(numValue);
      if (!filteredDistribution[key]) {
        filteredDistribution[key] = { count: 0, percentOfAnswered: 0, percentOfTotal: 0 };
      }
      filteredDistribution[key].count++;
      totalFilteredCount++;
      filteredNumericValues.push(numValue);
    });

    // Update percentages
    Object.keys(filteredDistribution).forEach(key => {
      filteredDistribution[key].percentOfAnswered = totalFilteredCount > 0
        ? (filteredDistribution[key].count / totalFilteredCount) * 100
        : 0;
      filteredDistribution[key].percentOfTotal = analytics.totalSurveyRespondents > 0
        ? (filteredDistribution[key].count / analytics.totalSurveyRespondents) * 100
        : 0;
    });

    // Calculate new numeric stats
    let newNumericStats = analytics.numericStats;
    if (filteredNumericValues.length > 0) {
      const sorted = [...filteredNumericValues].sort((a, b) => a - b);
      const sum = filteredNumericValues.reduce((a, b) => a + b, 0);
      const average = sum / filteredNumericValues.length;
      const n = filteredNumericValues.length;
      const median = n % 2 === 0
        ? (sorted[n / 2 - 1] + sorted[n / 2]) / 2
        : sorted[Math.floor(n / 2)];

      newNumericStats = {
        min: sorted[0],
        max: sorted[n - 1],
        average,
        median,
        stdDev: calculateStdDev(filteredNumericValues),
        mode: calculateMode(filteredNumericValues)
      };
    }

    return {
      ...analytics,
      distribution: filteredDistribution,
      numericStats: newNumericStats,
      responseCount: totalFilteredCount
    };
  }, [analytics, excludedOutliers, question.id, question.type, rawResponses]);

  const responseRate = analytics.eligibleRespondents > 0
    ? Math.round((analytics.responseCount / analytics.eligibleRespondents) * 100)
    : 0;

  const hasContent = analytics.responseCount > 0 ||
    (analytics.distribution && Object.keys(analytics.distribution).length > 0) ||
    (analytics.textResponses && analytics.textResponses.length > 0);

  const getLabelForValue = (value: string): string => {
    if (question.options && question.options.length > 0) {
      const option = question.options.find(opt => opt.value === value);
      if (option) return option.label;
    }
    return value.split(/[-_]/).map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
  };

  // Prepare chart data for choice questions - include ALL options even with 0 responses
  const getChartData = () => {
    const distribution = analytics.distribution || {};

    // For yes/no questions, always show both options
    if (question.type === 'yesno') {
      const yesNo = ['Yes', 'No'];
      return yesNo.map(value => {
        const entry = distribution[value];
        return {
          name: value,
          value: value,
          count: entry?.count || 0,
          percentOfAnswered: entry?.percentOfAnswered || 0,
          percentOfTotal: entry?.percentOfTotal || 0
        };
      });
    }

    // For questions with defined options, use all options
    if (question.options && question.options.length > 0) {
      return question.options.map(opt => {
        const entry = distribution[opt.value];
        return {
          name: opt.label,
          value: opt.value,
          count: entry?.count || 0,
          percentOfAnswered: entry?.percentOfAnswered || 0,
          percentOfTotal: entry?.percentOfTotal || 0
        };
      });
    }

    // Fallback: use distribution data only (for questions without predefined options)
    return Object.entries(distribution)
      .map(([key, entry]) => ({
        name: getLabelForValue(key),
        value: key,
        count: entry.count,
        percentOfAnswered: entry.percentOfAnswered,
        percentOfTotal: entry.percentOfTotal
      }))
      .sort((a, b) => b.count - a.count);
  };

  // Prepare numeric distribution data - include ALL values in the range (even with 0 responses)
  const getNumericDistributionData = () => {
    // Use filteredAnalytics for numeric questions when outliers are excluded
    const effectiveAnalytics = ['number', 'rating', 'scale'].includes(question.type)
      ? filteredAnalytics
      : analytics;

    if (!effectiveAnalytics.distribution) return [];

    const distribution = effectiveAnalytics.distribution;

    // Use the question's defined scale settings, with sensible defaults
    let scaleMin: number;
    let scaleMax: number;

    if (question.type === 'rating' || question.type === 'scale') {
      // Rating and Scale both default to 1-5 (matching survey builder defaults)
      scaleMin = question.settings?.minValue ?? 1;
      scaleMax = question.settings?.maxValue ?? 5;
    } else {
      // For number type, use observed values since it can be any range
      const observedValues = Object.keys(distribution).map(Number);
      scaleMin = Math.min(...observedValues);
      scaleMax = Math.max(...observedValues);
    }

    // For integer scales, show all values in range
    const isIntegerScale = Number.isInteger(scaleMin) && Number.isInteger(scaleMax) && (scaleMax - scaleMin) <= 10;

    if (isIntegerScale) {
      const data = [];
      for (let i = scaleMin; i <= scaleMax; i++) {
        const entry = distribution[String(i)];
        data.push({
          value: i,
          count: entry?.count || 0
        });
      }
      return data;
    }

    // For non-integer or large ranges, just use the distribution data
    return Object.entries(distribution)
      .map(([key, entry]) => ({ value: Number(key), count: entry.count }))
      .sort((a, b) => a.value - b.value);
  };

  return (
    <div className="py-3 px-1" style={{ borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
      {/* Question Header Row */}
      <div
        className="flex items-start justify-between cursor-pointer hover:bg-gray-50 rounded -mx-1 px-1 py-1 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex-1 min-w-0 pr-4">
          <div className="flex items-start gap-2">
            <span style={{ fontSize: '0.75rem', color: 'var(--color-muted)', fontWeight: 500, minWidth: '2rem' }}>
              Q{questionNumber}
            </span>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p style={{ fontSize: '0.875rem', color: 'var(--color-primary)', fontWeight: 500, lineHeight: 1.4 }}>
                  {analytics.questionTitle}
                </p>
                {/* Scale indicator for rating/scale questions - shows the question's defined scale */}
                {['rating', 'scale'].includes(question.type) && (
                  <span
                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs"
                    style={{ backgroundColor: 'rgba(199, 161, 74, 0.15)', color: '#b8860b', fontWeight: 500 }}
                  >
                    Scale: {question.settings?.minValue ?? 1}-{question.settings?.maxValue ?? 5}
                  </span>
                )}
              </div>
              {/* Inline response context */}
              <div className="flex items-center gap-3 mt-1">
                <span style={{ fontSize: '0.75rem', color: responseRate >= 80 ? '#059669' : responseRate >= 50 ? '#D97706' : '#DC2626' }}>
                  {analytics.responseCount}/{analytics.eligibleRespondents} ({responseRate}%)
                </span>
                {analytics.skipCount > 0 && (
                  <span style={{ fontSize: '0.75rem', color: 'var(--color-muted)' }}>
                    {analytics.skipCount} skipped
                  </span>
                )}
                {analytics.conditionalContext?.isConditional && (
                  <span style={{ fontSize: '0.75rem', color: '#6366F1', fontStyle: 'italic' }}>
                    {analytics.conditionalContext.description}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Quick Stats & Expand */}
        <div className="flex items-center gap-3 flex-shrink-0">
          {/* Quick Summary based on type */}
          {['radio', 'dropdown', 'yesno', 'checkbox'].includes(question.type) && analytics.distribution && (
            <div style={{ fontSize: '0.75rem', color: 'var(--color-muted)', textAlign: 'right' }}>
              {Object.entries(analytics.distribution)
                .sort(([,a], [,b]) => b.count - a.count)
                .slice(0, 1)
                .map(([key, entry]) => (
                  <span key={key}>
                    Top: <strong style={{ color: 'var(--color-primary)' }}>{getLabelForValue(key)}</strong> ({entry.count})
                  </span>
                ))}
            </div>
          )}

          {['number', 'rating', 'scale'].includes(question.type) && filteredAnalytics.numericStats && (
            <div style={{ fontSize: '0.75rem', color: 'var(--color-muted)', textAlign: 'right' }}>
              Avg: <strong style={{ color: 'var(--color-accent)' }}>{filteredAnalytics.numericStats.average.toFixed(1)}</strong>
              {excludedOutliers.size > 0 && (
                <span style={{ color: '#dc2626', marginLeft: '4px' }}>(filtered)</span>
              )}
            </div>
          )}

          {['text', 'textarea', 'email'].includes(question.type) && (
            <div style={{ fontSize: '0.75rem', color: 'var(--color-muted)' }}>
              {analytics.textResponses?.length || 0} responses
            </div>
          )}

          {/* Chart Type Selector */}
          <ChartTypeSelector
            questionType={question.type}
            selectedChart={chartType}
            onChartChange={onChartTypeChange}
          />

          <svg
            className="w-4 h-4 transition-transform"
            style={{ color: 'var(--color-muted)', transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
            fill="none" stroke="currentColor" viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>

      {/* Expanded Content */}
      {expanded && hasContent && (
        <div className="mt-3 ml-8 pl-2" style={{ borderLeft: '2px solid var(--color-accent)' }}>

          {/* Question-level Insights */}
          {questionInsights.length > 0 && (
            <div className="mb-3 p-2 rounded-lg" style={{ backgroundColor: 'rgba(199, 161, 74, 0.08)' }}>
              {questionInsights.slice(0, 2).map(insight => (
                <div key={insight.id} className="flex items-start gap-2 text-xs mb-1.5 last:mb-0" style={{ color: 'var(--color-primary)' }}>
                  <span className="flex-shrink-0 mt-0.5" style={{ color: insight.priority === 'high' ? '#DC2626' : insight.priority === 'medium' ? '#F59E0B' : 'var(--color-accent)' }}>
                    {insight.priority === 'high' ? '!' : insight.priority === 'medium' ? '•' : '○'}
                  </span>
                  <span className="flex-1" style={{ wordBreak: 'break-word' }}>
                    <strong>{insight.title}:</strong> {insight.description}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* Choice Questions (Radio, Dropdown, Checkbox, YesNo) */}
          {['radio', 'dropdown', 'yesno', 'checkbox'].includes(question.type) && analytics.distribution && (
            <div className="py-2">
              {/* Pie/Donut Chart */}
              {(chartType === 'pie' || chartType === 'donut') && (
                <div className="flex justify-center">
                  <ResponsiveContainer width={320} height={220}>
                    <PieChart>
                      <Pie
                        data={getChartData()}
                        dataKey="count"
                        nameKey="name"
                        cx="50%"
                        cy="45%"
                        innerRadius={chartType === 'donut' ? 40 : 0}
                        outerRadius={65}
                        label={({ percent }) => `${(Number(percent ?? 0) * 100).toFixed(0)}%`}
                        labelLine={{ stroke: '#999', strokeWidth: 1 }}
                        fontSize={11}
                      >
                        {getChartData().map((_, index) => (
                          <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(value, name) => [`${value} responses`, name]}
                        contentStyle={{ backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '6px', fontSize: '12px', padding: '8px 12px' }}
                      />
                      <Legend
                        wrapperStyle={{ fontSize: '11px', paddingTop: '10px' }}
                        layout="horizontal"
                        align="center"
                        verticalAlign="bottom"
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Vertical Bar Chart */}
              {chartType === 'bar' && (
                <ResponsiveContainer width="100%" height={Math.max(getChartData().length * 40, 120)}>
                  <BarChart data={getChartData()} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                    <XAxis dataKey="name" tick={{ fontSize: 10, fill: 'var(--color-muted)' }} tickLine={false} axisLine={false} />
                    <YAxis hide />
                    <Tooltip
                      formatter={(value, _name, props) => {
                        const payload = props?.payload as { percentOfAnswered: number } | undefined;
                        return [`${value} (${payload?.percentOfAnswered?.toFixed(0) || 0}%)`, ''];
                      }}
                      contentStyle={{ backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '6px', fontSize: '12px', padding: '8px 12px' }}
                    />
                    <Bar dataKey="count" radius={[4, 4, 0, 0]} maxBarSize={50}>
                      {getChartData().map((_, index) => (
                        <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                      ))}
                      <LabelList dataKey="count" position="top" style={{ fontSize: '11px', fontWeight: 600, fill: 'var(--color-primary)' }} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}

              {/* Horizontal Bar Chart (default) */}
              {chartType === 'horizontalBar' && (
                <div>
                  <ResponsiveContainer width="100%" height={Math.max(getChartData().length * 32, 80)}>
                    <BarChart data={getChartData()} layout="vertical" margin={{ left: 0, right: 50 }}>
                      <XAxis type="number" hide />
                      <YAxis
                        type="category"
                        dataKey="name"
                        width={140}
                        tick={{ fontSize: 11, fill: 'var(--color-primary)' }}
                        tickLine={false}
                        axisLine={false}
                      />
                      <Tooltip
                        formatter={(value, _name, props) => {
                          const payload = props?.payload as { percentOfAnswered: number; percentOfTotal: number } | undefined;
                          if (payload) {
                            return [`${value} (${payload.percentOfAnswered.toFixed(0)}% of responses)`, ''];
                          }
                          return [String(value), ''];
                        }}
                        contentStyle={{ backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '6px', fontSize: '12px', padding: '8px 12px' }}
                      />
                      <Bar dataKey="count" radius={[0, 3, 3, 0]} maxBarSize={20}>
                        {getChartData().map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.count > 0 ? '#C7A14A' : '#E5E7EB'} />
                        ))}
                        <LabelList
                          dataKey="count"
                          position="right"
                          style={{ fontSize: '11px', fontWeight: 600, fill: 'var(--color-primary)' }}
                        />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Detailed breakdown */}
              <div className="mt-3 space-y-1">
                {getChartData().map(item => (
                  <div key={item.value} className="flex items-center justify-between text-xs" style={{ color: 'var(--color-muted)' }}>
                    <span>{item.name}</span>
                    <span>
                      <strong style={{ color: 'var(--color-primary)' }}>{item.count}</strong>
                      {' '}({item.percentOfAnswered.toFixed(0)}% of answered / {item.percentOfTotal.toFixed(0)}% of total)
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Numeric Questions (Number, Rating, Scale) */}
          {['number', 'rating', 'scale'].includes(question.type) && filteredAnalytics.numericStats && (
            <div className="py-2">
              {/* Filtered indicator */}
              {excludedOutliers.size > 0 && (
                <div
                  className="flex items-center gap-2 mb-3 py-1.5 px-2.5 rounded"
                  style={{ backgroundColor: 'rgba(220, 38, 38, 0.08)', border: '1px solid rgba(220, 38, 38, 0.2)' }}
                >
                  <svg width="14" height="14" viewBox="0 0 20 20" fill="#dc2626">
                    <path fillRule="evenodd" d="M3 3a1 1 0 011-1h12a1 1 0 011 1v3a1 1 0 01-.293.707L12 11.414V15a1 1 0 01-.293.707l-2 2A1 1 0 018 17v-5.586L3.293 6.707A1 1 0 013 6V3z" clipRule="evenodd" />
                  </svg>
                  <span style={{ fontSize: '0.75rem', color: '#dc2626', fontWeight: 500 }}>
                    {excludedOutliers.size} outlier{excludedOutliers.size !== 1 ? 's' : ''} excluded
                  </span>
                  <button
                    onClick={(e) => { e.stopPropagation(); onClearOutliers(); }}
                    style={{
                      marginLeft: 'auto',
                      fontSize: '0.6875rem',
                      color: '#dc2626',
                      textDecoration: 'underline',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer'
                    }}
                  >
                    Clear
                  </button>
                </div>
              )}

              {/* Stats Row */}
              <div className="flex items-center gap-4 mb-3 flex-wrap">
                <div>
                  <span style={{ fontSize: '0.625rem', color: 'var(--color-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>AVG</span>
                  <div style={{ fontSize: '1.125rem', fontWeight: 700, color: 'var(--color-accent)' }}>
                    {filteredAnalytics.numericStats.average.toFixed(1)}
                  </div>
                </div>
                <div>
                  <span style={{ fontSize: '0.625rem', color: 'var(--color-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>MED</span>
                  <div style={{ fontSize: '1.125rem', fontWeight: 600, color: 'var(--color-primary)' }}>
                    {filteredAnalytics.numericStats.median.toFixed(1)}
                  </div>
                </div>
                <div>
                  <span style={{ fontSize: '0.625rem', color: 'var(--color-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>STD DEV</span>
                  <div style={{ fontSize: '1.125rem', fontWeight: 600, color: '#8B5CF6' }}>
                    {filteredAnalytics.numericStats.stdDev.toFixed(2)}
                  </div>
                </div>
                <div>
                  <span style={{ fontSize: '0.625rem', color: 'var(--color-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>MODE</span>
                  <div style={{ fontSize: '1.125rem', fontWeight: 600, color: '#3B82F6' }}>
                    {filteredAnalytics.numericStats.mode}
                  </div>
                </div>
                <div>
                  <span style={{ fontSize: '0.625rem', color: 'var(--color-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>RANGE</span>
                  <div style={{ fontSize: '1.125rem', fontWeight: 600, color: 'var(--color-primary)' }}>
                    {filteredAnalytics.numericStats.min} - {filteredAnalytics.numericStats.max}
                  </div>
                </div>
              </div>

              {/* Range Indicator */}
              <div className="mb-3">
                <div className="relative h-2 rounded-full" style={{ backgroundColor: '#E5E7EB' }}>
                  <div
                    className="absolute h-full rounded-full"
                    style={{
                      backgroundColor: 'rgba(199, 161, 74, 0.4)',
                      left: '0%',
                      width: '100%'
                    }}
                  />
                  {/* Average marker */}
                  <div
                    className="absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full border-2 border-white"
                    style={{
                      backgroundColor: 'var(--color-accent)',
                      left: `${((filteredAnalytics.numericStats.average - filteredAnalytics.numericStats.min) / (filteredAnalytics.numericStats.max - filteredAnalytics.numericStats.min || 1)) * 100}%`,
                      transform: 'translateX(-50%) translateY(-50%)'
                    }}
                  />
                </div>
                {/* Labels - Min/Max at edges, Avg positioned under dot */}
                <div className="relative mt-1" style={{ fontSize: '0.625rem', color: 'var(--color-muted)' }}>
                  <span className="absolute left-0">Min: {filteredAnalytics.numericStats.min}</span>
                  <span
                    className="absolute"
                    style={{
                      color: 'var(--color-accent)',
                      left: `${((filteredAnalytics.numericStats.average - filteredAnalytics.numericStats.min) / (filteredAnalytics.numericStats.max - filteredAnalytics.numericStats.min || 1)) * 100}%`,
                      transform: 'translateX(-50%)',
                      fontWeight: 600
                    }}
                  >
                    Avg: {filteredAnalytics.numericStats.average.toFixed(1)}
                  </span>
                  <span className="absolute right-0">Max: {filteredAnalytics.numericStats.max}</span>
                  {/* Spacer for height */}
                  <span className="invisible">Min: 0</span>
                </div>
              </div>

              {/* Distribution Chart */}
              {filteredAnalytics.distribution && Object.keys(filteredAnalytics.distribution).length > 0 && (
                <div>
                  <p style={{ fontSize: '0.75rem', fontWeight: 500, color: 'var(--color-muted)', marginBottom: '0.5rem' }}>Distribution</p>

                  {/* Bar Chart (default) */}
                  {chartType === 'bar' && (
                    <ResponsiveContainer width="100%" height={120}>
                      <BarChart
                        data={getNumericDistributionData()}
                        margin={{ top: 20, right: 10, left: 10, bottom: 5 }}
                      >
                        <XAxis dataKey="value" tick={{ fontSize: 10, fill: 'var(--color-muted)' }} tickLine={false} axisLine={false} />
                        <YAxis hide />
                        <Tooltip
                          contentStyle={{ backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '6px', fontSize: '12px', padding: '6px 10px' }}
                        />
                        <Bar dataKey="count" radius={[3, 3, 0, 0]} maxBarSize={40}>
                          {getNumericDistributionData().map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.count > 0 ? '#C7A14A' : '#E5E7EB'} />
                          ))}
                          <LabelList
                            dataKey="count"
                            position="top"
                            style={{ fontSize: '11px', fontWeight: 600, fill: 'var(--color-primary)' }}
                          />
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  )}

                  {/* Line Chart */}
                  {chartType === 'line' && (
                    <ResponsiveContainer width="100%" height={120}>
                      <LineChart
                        data={getNumericDistributionData()}
                        margin={{ top: 20, right: 10, left: 10, bottom: 5 }}
                      >
                        <XAxis dataKey="value" tick={{ fontSize: 10, fill: 'var(--color-muted)' }} tickLine={false} axisLine={false} />
                        <YAxis hide />
                        <Tooltip
                          contentStyle={{ backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '6px', fontSize: '12px', padding: '6px 10px' }}
                        />
                        <Line
                          type="monotone"
                          dataKey="count"
                          stroke="#C7A14A"
                          strokeWidth={2}
                          dot={{ fill: '#C7A14A', strokeWidth: 2, r: 4 }}
                          activeDot={{ r: 6, fill: '#C7A14A' }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  )}

                  {/* Area Chart */}
                  {chartType === 'area' && (
                    <ResponsiveContainer width="100%" height={120}>
                      <AreaChart
                        data={getNumericDistributionData()}
                        margin={{ top: 20, right: 10, left: 10, bottom: 5 }}
                      >
                        <XAxis dataKey="value" tick={{ fontSize: 10, fill: 'var(--color-muted)' }} tickLine={false} axisLine={false} />
                        <YAxis hide />
                        <Tooltip
                          contentStyle={{ backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '6px', fontSize: '12px', padding: '6px 10px' }}
                        />
                        <Area
                          type="monotone"
                          dataKey="count"
                          stroke="#C7A14A"
                          fill="rgba(199, 161, 74, 0.3)"
                          strokeWidth={2}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Text Questions */}
          {['text', 'textarea', 'email'].includes(question.type) && analytics.textResponses && (
            <div className="py-2">
              {analytics.textResponses.length > 0 ? (
                <>
                  {/* Check if responses are mostly short (< 30 chars avg) */}
                  {(() => {
                    const avgLength = analytics.textResponses.reduce((sum, r) => sum + r.text.length, 0) / analytics.textResponses.length;
                    const isShortResponses = avgLength < 30;
                    const responsesToShow = showAllText ? analytics.textResponses : analytics.textResponses.slice(0, isShortResponses ? 12 : 3);

                    return isShortResponses ? (
                      // Compact tag layout for short responses
                      <>
                        <div className="flex flex-wrap gap-1.5">
                          {responsesToShow.map((response, idx) => (
                            <span
                              key={idx}
                              className="inline-flex items-center px-2.5 py-1 rounded-md"
                              style={{
                                backgroundColor: 'rgba(199, 161, 74, 0.1)',
                                fontSize: '0.8125rem',
                                color: 'var(--color-primary)'
                              }}
                              title={`Response #${response.responseIndex} (${response.wordCount} words)`}
                            >
                              {response.text}
                            </span>
                          ))}
                        </div>
                        {analytics.textResponses.length > 12 && (
                          <button
                            onClick={(e) => { e.stopPropagation(); setShowAllText(!showAllText); }}
                            className="mt-2"
                            style={{ fontSize: '0.75rem', fontWeight: 500, color: 'var(--color-accent)' }}
                          >
                            {showAllText ? 'Show less' : `+${analytics.textResponses.length - 12} more`}
                          </button>
                        )}
                      </>
                    ) : (
                      // Quote card layout for longer responses
                      <>
                        <div className="space-y-1.5">
                          {responsesToShow.map((response, idx) => (
                            <div
                              key={idx}
                              className="py-1.5 px-2.5 rounded flex items-start gap-2"
                              style={{ backgroundColor: 'rgba(0,0,0,0.02)' }}
                            >
                              <span
                                className="flex-shrink-0 text-center rounded"
                                style={{
                                  fontSize: '0.625rem',
                                  fontWeight: 600,
                                  color: 'var(--color-accent)',
                                  minWidth: '20px'
                                }}
                              >
                                #{response.responseIndex}
                              </span>
                              <p
                                style={{
                                  fontSize: '0.8125rem',
                                  color: 'var(--color-primary)',
                                  lineHeight: 1.4,
                                  fontStyle: 'italic',
                                  margin: 0
                                }}
                                title={`${response.wordCount} words`}
                              >
                                "{response.text}"
                              </p>
                            </div>
                          ))}
                        </div>
                        {analytics.textResponses.length > 3 && (
                          <button
                            onClick={(e) => { e.stopPropagation(); setShowAllText(!showAllText); }}
                            className="mt-2"
                            style={{ fontSize: '0.75rem', fontWeight: 500, color: 'var(--color-accent)' }}
                          >
                            {showAllText ? 'Show less' : `+${analytics.textResponses.length - 3} more responses`}
                          </button>
                        )}
                      </>
                    );
                  })()}
                </>
              ) : (
                <p style={{ fontSize: '0.875rem', color: 'var(--color-muted)', fontStyle: 'italic' }}>No text responses</p>
              )}
            </div>
          )}

          {/* No responses message */}
          {!hasContent && (
            <p style={{ fontSize: '0.875rem', color: 'var(--color-muted)', fontStyle: 'italic', padding: '0.5rem 0' }}>
              No responses for this question
            </p>
          )}

          {/* View All Individual Responses Button and Detect Outliers */}
          {analytics.responseCount > 0 && (
            <div className="mt-4 pt-3 flex flex-wrap items-center gap-2" style={{ borderTop: '1px dashed rgba(0,0,0,0.1)' }}>
              <button
                onClick={(e) => { e.stopPropagation(); setShowAllResponses(true); }}
                className="inline-flex items-center gap-1.5 rounded-md transition-colors"
                style={{
                  backgroundColor: 'rgba(43, 122, 120, 0.1)',
                  color: '#2B7A78',
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  padding: '6px 10px'
                }}
              >
                <svg style={{ width: '14px', height: '14px', flexShrink: 0 }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                <span>View all {analytics.responseCount} individual responses</span>
              </button>

              {/* Detect Outliers Button - only for numeric questions */}
              {['number', 'rating', 'scale'].includes(question.type) && filteredAnalytics.numericStats && (
                <button
                  onClick={(e) => { e.stopPropagation(); onOpenOutlierModal(); }}
                  className="inline-flex items-center gap-1.5 rounded-md transition-colors"
                  style={{
                    backgroundColor: excludedOutliers.size > 0
                      ? 'rgba(220, 38, 38, 0.1)'
                      : 'rgba(139, 92, 246, 0.1)',
                    color: excludedOutliers.size > 0 ? '#dc2626' : '#8b5cf6',
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    padding: '6px 10px'
                  }}
                >
                  <svg style={{ width: '14px', height: '14px', flexShrink: 0 }} viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M3 3a1 1 0 011-1h12a1 1 0 011 1v3a1 1 0 01-.293.707L12 11.414V15a1 1 0 01-.293.707l-2 2A1 1 0 018 17v-5.586L3.293 6.707A1 1 0 013 6V3z" clipRule="evenodd" />
                  </svg>
                  <span>
                    {excludedOutliers.size > 0
                      ? `${excludedOutliers.size} outlier${excludedOutliers.size !== 1 ? 's' : ''} excluded`
                      : 'Detect Outliers'}
                  </span>
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Individual Responses Modal */}
      {showAllResponses && (
        <IndividualResponsesModal
          question={question}
          rawResponses={rawResponses}
          onClose={() => setShowAllResponses(false)}
        />
      )}
    </div>
  );
};

// Modal to show all individual responses for a question
interface IndividualResponsesModalProps {
  question: QuestionDefinition;
  rawResponses: DynamicSurveyResponse[];
  onClose: () => void;
}

const IndividualResponsesModal: React.FC<IndividualResponsesModalProps> = ({ question, rawResponses, onClose }) => {
  const formatValue = (value: any): string => {
    if (value === null || value === undefined || value === '') return '-';
    if (question.type === 'yesno') return value === true || value === 'Yes' || value === 'yes' ? 'Yes' : 'No';
    if (Array.isArray(value)) return value.join(', ');
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    return String(value);
  };

  const formatDateTime = (timestamp: any): string => {
    if (!timestamp) return '-';
    try {
      let date: Date;
      if (timestamp.toDate && typeof timestamp.toDate === 'function') {
        date = timestamp.toDate();
      } else if (timestamp._seconds !== undefined) {
        date = new Date(timestamp._seconds * 1000);
      } else {
        date = new Date(timestamp);
      }
      if (isNaN(date.getTime())) return '-';
      const dateStr = date.toLocaleDateString('en-AU', { day: '2-digit', month: 'short' });
      const timeStr = date.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', hour12: true });
      return `${dateStr} ${timeStr}`;
    } catch {
      return '-';
    }
  };

  // Get all responses for this question with respondent info
  const allIndividualResponses = rawResponses.map((r, idx) => {
    const response = r.responses[question.id];
    const value = response?.value;
    const hasResponse = value !== null && value !== undefined && value !== '' && !response?.skipped;
    return {
      respondentNumber: idx + 1,
      value: hasResponse ? formatValue(value) : null,
      submittedAt: r.metadata?.submittedAt,
      hasResponse
    };
  });

  const answeredResponses = allIndividualResponses.filter(r => r.hasResponse);
  const skippedResponses = allIndividualResponses.filter(r => !r.hasResponse);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-lg"
        style={{ maxHeight: '80vh' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-4 py-3 border-b flex items-center justify-between" style={{ borderColor: 'var(--color-border)', backgroundColor: '#f8f9fa' }}>
          <div className="flex-1 min-w-0 pr-4">
            <p className="text-sm font-semibold truncate" style={{ color: 'var(--color-primary)' }} title={question.title}>
              {question.title}
            </p>
            <p className="text-xs mt-0.5" style={{ color: 'var(--color-muted)' }}>
              {answeredResponses.length} answered · {skippedResponses.length} skipped/not shown
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-7 h-7 flex items-center justify-center rounded hover:bg-gray-200 transition-colors flex-shrink-0"
            style={{ color: 'var(--color-muted)' }}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto" style={{ maxHeight: 'calc(80vh - 120px)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#fafafa' }}>
                <th className="px-3 py-2 text-left" style={{ fontSize: '0.6875rem', fontWeight: 600, color: 'var(--color-muted)', width: '60px' }}>
                  #
                </th>
                <th className="px-3 py-2 text-left" style={{ fontSize: '0.6875rem', fontWeight: 600, color: 'var(--color-muted)' }}>
                  Response
                </th>
                <th className="px-3 py-2 text-right" style={{ fontSize: '0.6875rem', fontWeight: 600, color: 'var(--color-muted)', width: '100px' }}>
                  Date/Time
                </th>
              </tr>
            </thead>
            <tbody>
              {allIndividualResponses.map((item, idx) => (
                <tr
                  key={idx}
                  style={{
                    backgroundColor: idx % 2 === 0 ? 'white' : '#fafafa',
                    borderBottom: '1px solid rgba(0,0,0,0.05)'
                  }}
                >
                  <td className="px-3 py-2" style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--color-accent)' }}>
                    {item.respondentNumber}
                  </td>
                  <td className="px-3 py-2" style={{
                    fontSize: '0.8125rem',
                    color: item.hasResponse ? 'var(--color-primary)' : 'var(--color-muted)',
                    fontStyle: item.hasResponse ? 'normal' : 'italic'
                  }}>
                    {item.hasResponse ? item.value : '(skipped)'}
                  </td>
                  <td className="px-3 py-2 text-right" style={{ fontSize: '0.6875rem', color: 'var(--color-muted)' }}>
                    {formatDateTime(item.submittedAt)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="px-4 py-2 border-t flex justify-end" style={{ borderColor: 'var(--color-border)' }}>
          <button
            onClick={onClose}
            className="text-xs font-medium px-4 py-1.5 rounded transition-colors"
            style={{ backgroundColor: 'var(--color-accent)', color: 'white' }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChartInsights;
